/**
 * sga-email-copy.js
 *
 * Adds a "copy to clipboard" button next to every email (mailto:) link inside the
 * contact-form component (the soundsgood.cz/pages/kontakt e-mail addresses).
 *
 * Why: clicking a mailto: link does nothing for visitors whose browser has no mail
 * client registered for the mailto: protocol (common on desktop Chrome/Firefox without
 * a configured handler). The copy button lets them grab the address regardless. The
 * original mailto: link is left untouched for everyone who does have a mail client.
 *
 * Self-contained, no dependencies. Injects its own styles once. Idempotent and safe to
 * run again after Shopify Section Rendering API re-renders (theme editor, dynamic loads).
 */
(function () {
  'use strict';

  var STYLE_ID = 'sga-email-copy-styles';
  var ENHANCED_FLAG = 'sgaCopyEnhanced';
  var SCOPE_SELECTOR = '.sga-contact-form a[href^="mailto:"]';

  var COPY_LABEL = 'Kopírovat e-mail';
  var COPIED_LABEL = 'Zkopírováno!';
  var FAIL_LABEL = 'Nelze zkopírovat';

  var COPY_ICON =
    '<svg class="sga-email-copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>' +
    '<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';

  var CHECK_ICON =
    '<svg class="sga-email-copy-check" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<polyline points="20 6 9 17 4 12"></polyline></svg>';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.sga-email-copy-wrap{display:inline-flex;align-items:center;gap:.5rem;position:relative;}' +
      '.sga-email-copy-btn{display:inline-flex;align-items:center;justify-content:center;' +
      'width:1.75rem;height:1.75rem;padding:0;margin:0;border:0;border-radius:.375rem;' +
      'background:transparent;color:inherit;cursor:pointer;opacity:.65;position:relative;' +
      '-webkit-appearance:none;appearance:none;transition:opacity .2s ease,background-color .2s ease;}' +
      '.sga-email-copy-btn:hover,.sga-email-copy-btn:focus-visible{opacity:1;' +
      'background-color:rgba(255,255,255,.14);outline:none;}' +
      '.sga-email-copy-icon,.sga-email-copy-check{width:1rem;height:1rem;display:block;}' +
      '.sga-email-copy-check{display:none;}' +
      '.sga-email-copy-btn.is-copied .sga-email-copy-icon{display:none;}' +
      '.sga-email-copy-btn.is-copied .sga-email-copy-check{display:block;color:#75fb9f;}' +
      '.sga-email-copy-tooltip{position:absolute;bottom:calc(100% + .5rem);left:50%;' +
      'transform:translateX(-50%) translateY(.25rem);white-space:nowrap;pointer-events:none;' +
      'background:#75fb9f;color:#191b19;font-size:.75rem;font-weight:600;line-height:1;' +
      'padding:.375rem .5rem;border-radius:.375rem;opacity:0;z-index:5;' +
      'transition:opacity .2s ease,transform .2s ease;}' +
      '.sga-email-copy-btn.is-active .sga-email-copy-tooltip{opacity:1;' +
      'transform:translateX(-50%) translateY(0);}';
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  function extractEmail(link) {
    var href = link.getAttribute('href') || '';
    return href.replace(/^mailto:/i, '').split('?')[0].trim();
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) resolve();
        else reject(new Error('execCommand copy failed'));
      } catch (err) {
        reject(err);
      }
    });
  }

  function enhance(link) {
    if (link.dataset[ENHANCED_FLAG] === 'true') return;
    var email = extractEmail(link);
    if (!email) return;
    link.dataset[ENHANCED_FLAG] = 'true';

    var wrap = document.createElement('span');
    wrap.className = 'sga-email-copy-wrap';
    link.parentNode.insertBefore(wrap, link);
    wrap.appendChild(link);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sga-email-copy-btn';
    btn.setAttribute('aria-label', COPY_LABEL + ': ' + email);
    btn.setAttribute('title', COPY_LABEL);
    btn.innerHTML =
      COPY_ICON + CHECK_ICON +
      '<span class="sga-email-copy-tooltip" role="status" aria-live="polite"></span>';

    var tooltip = btn.querySelector('.sga-email-copy-tooltip');
    var timer = null;

    function flash(message, copied) {
      tooltip.textContent = message;
      btn.classList.add('is-active');
      if (copied) btn.classList.add('is-copied');
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        btn.classList.remove('is-active');
        btn.classList.remove('is-copied');
        tooltip.textContent = '';
      }, 2000);
    }

    btn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      copyText(email).then(function () {
        flash(COPIED_LABEL, true);
      }).catch(function () {
        flash(FAIL_LABEL, false);
      });
    });

    wrap.appendChild(btn);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var links = scope.querySelectorAll(SCOPE_SELECTOR);
    for (var i = 0; i < links.length; i++) enhance(links[i]);
  }

  function init() {
    injectStyles();
    scan(document);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SCOPE_SELECTOR)) enhance(node);
          if (node.querySelectorAll) scan(node);
        }
      }
    });
    var target = document.body || document.documentElement;
    observer.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
